import { IMedia } from '@dtos/Course'
import { useDomain } from '@hooks/useThemeParams'
import { useThemeColor } from '@hooks/useThemeColor'
import { PdfViewer } from './PdfViewer'
import { processHtmlContent } from '@utils/processHTMLContent'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react'
import { StyleSheet, Dimensions } from 'react-native'
import RenderHtml from 'react-native-render-html'
import YouTube, { YouTubeEvent, YouTubePlayer } from 'react-youtube'
import { Image, getToken, isWeb, useMedia, View, Spinner, Text } from 'tamagui'
import { Button } from '@components/Button'
import { HeroIcon } from '@components/HeroIcon'
import { useFirstValuePerKey } from '@hooks/useFirstValuePerKey'
import { useQuarterProgressReporter } from '@hooks/useQuarterProgressReporter'
import { useSparkVideosIframeProgress } from '@hooks/useSparkVideosIframeProgress'
import { useVimeoIframeProgress } from '@hooks/useVimeoIframeProgress'
import { parseVimeoSource, buildVimeoPlayerUrl } from '@utils/vimeoSource'
import { ZoomableImage } from './ZoomableImage'
import { VideoPlayer } from './VideoPlayer'
import { useMediaSize } from '@hooks/useMediaSize'
import { sparkVideosClient } from '@services/api/sparkvideos'
import { generateVideoQualityOptions } from '@utils/videoQualityOptions'
import { useMediaRenderWrapperDynamicHeight } from '@hooks/useMediaRenderWrapperDynamicHeight'
import { YoutubeVideoPlayer } from './YoutubeVideoPlayer'
import { VimeoVideoPlayer } from './VimeoVideoPlayer'
import { AudioPlayer } from './AudioPlayer'
import { NotSupported } from './enrollments/courses/extra_contents/NotSupported'
import WebView from 'react-native-webview'
import {
  MEDIA_TYPES,
  EMBED_SANDBOX,
  isDownloadOnlyFileExt,
} from '@constants/mediaTypes'
import {
  htmlIframeElementModels,
  htmlIframeRenderers,
} from './HtmlIframeRenderer'
import { MoviieLessonPlayer } from './MoviieLessonPlayer'
import { FloatingExpandButton } from './FloatingExpandButton'
import { useImageViewerNavigation } from '@hooks/useImageViewerNavigation'
import { useLocalSearchParams } from 'expo-router'
import { ProcessingVideoInformative } from './ProcessingVideoInformative'
import { SparkVideosPlayerIframe } from './SparkVideosPlayerIframe'
import { buildPdfViewerUrl } from '@utils/pdfViewerUrl'

export interface IUpdateLessonProgress {
  progress: number
  currentVideoTime: number
}

interface IMediaRenderProps {
  media: IMedia
  children?: ReactNode
  thumbnailUrl?: string
  isImageVariant?: boolean
  isExpandedViewPage?: boolean
  closeModal?: () => void
  location?: 'extraContent' | 'courseContent'
  fileUrl?: string
  updateLessonProgress?: (params: IUpdateLessonProgress) => void
  startMediaFrom?: number
  progress?: number
  isLoading?: boolean
  /** Altura fixa do player de vídeo no web (consumo desktop). Largura preenche
   * 100% do espaço disponível. Ex.: lista fechada 647, aberta 446. */
  playerFillHeight?: number
}

export interface ISparkVideosUrls {
  mainUrl: string
  qualities: {
    [key: number]: string
  }
  thumbnailUrl: string
  isVertical: boolean
  width: number
  height: number
}

// Proporção padrão dos players de vídeo no web. Serve de altura quando o
// layout não impõe uma (ver `webVideoContainerStyle`).
const WEB_VIDEO_ASPECT_RATIO = 16 / 9

// Converte URLs "watch" para a versão embedável. O YouTube recusa iframes de
// /watch?v= e youtu.be/ (X-Frame-Options); só /embed/ID é permitido. Vimeo idem
// (player.vimeo.com). Para outras URLs, retorna como está.
const toEmbeddableUrl = (url: string): string => {
  const youtube = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/,
  )
  if (youtube) return `https://www.youtube.com/embed/${youtube[1]}`

  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/)
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`

  return url
}

// O HTML colado pelo produtor ("HTML livre") normalmente não é um documento
// completo (sem <html>/<head>), então o srcDoc/WebView cria um body implícito
// que herda o user-agent stylesheet do browser (margin/border padrão) — some
// esse reset antes do conteúdo colado; <style> funciona em qualquer posição.
const withEmbedReset = (html: string) =>
  `<style>html,body{margin:0;padding:0;border:0}</style>${html}`

export function MediaRender({
  media,
  children,
  isImageVariant = false,
  isExpandedViewPage = false,
  thumbnailUrl,
  closeModal,
  location,
  fileUrl,
  updateLessonProgress,
  startMediaFrom,
  progress,
  isLoading = false,
  playerFillHeight,
}: IMediaRenderProps) {
  const { enrollmentId, courseId, courseContentsId } = useLocalSearchParams()
  const isMediaSparkVideos =
    media.host === MEDIA_TYPES.SPARK_VIDEOS && media.hostKey && media.id
  const [isLoadingVideo, setIsLoadingVideo] = useState(
    Boolean(isMediaSparkVideos),
  )
  const [sparkVideosUrls, setSparkVideosUrls] =
    useState<ISparkVideosUrls | null>(null)
  const [sparkVideosError, setSparkVideosError] = useState(false)
  const [textContentWidth, setTextContentWidth] = useState(0)
  const textBodyColor = useThemeColor('text-body')
  const screenMedia = useMedia()
  const horizontalPadding = media.type === 'Image' ? 0 : 40
  const windowWidth = Dimensions.get('window').width
  const windowHeight = Dimensions.get('window').height
  const canShowVideo = !isLoadingVideo && sparkVideosUrls !== null

  const mediaSize = useMediaSize()
  const mediaRenderWrapperHeight = useMediaRenderWrapperDynamicHeight({
    mediaSize,
    mediaType: media.type,
    isExpanded: isExpandedViewPage,
    textHeight: 0,
    location,
    fileExtension: media.fileExt,
  })
  const videoBorderRadius = getToken('$border-radius-md')
  const isMediaPdf = media?.fileExt?.toLowerCase() === '.pdf'

  // Planilha e Word não têm preview embutido: os dois caem no mesmo card do
  // NotSupported, que entrega o download no layout padrão.
  const isDownloadOnlyFile = useMemo(
    () => isDownloadOnlyFileExt(media.fileExt),
    [media.fileExt],
  )

  const needFloatingExpandButton = useMemo(() => {
    return media.type === 'Image' && location === 'extraContent' && !isWeb
  }, [media.type, location])

  const { handleNavigateToImageViewer } = useImageViewerNavigation({
    enrollmentId: enrollmentId?.toString() || '',
    courseId: courseId?.toString() || '',
    courseContentsId: courseContentsId?.toString() || '',
    isExtraContent: location === 'extraContent',
  })

  // Player de vídeo no web: preenche 100% da largura disponível. Não afeta
  // nativo nem extraContent.
  const isWebCourseVideo =
    isWeb && media?.type === 'Video' && location !== 'extraContent'

  const { width, height } = useMemo(() => {
    const computedWidth = screenMedia.gtSm
      ? screenMedia.gtMd
        ? 958
        : 576
      : windowWidth - horizontalPadding
    const computedHeight = (() => {
      // Nativo: o MediaRenderWrapper trava a altura do vídeo e corta o que
      // passar (`overflow: hidden`), então a caixa do player tem de ser a
      // mesma: a reservada pelo layout ou a do wrapper. Com
      // `windowHeight * 0.6` no tablet o player por WebView (Panda, embed,
      // webinar, Vimeo, Moviie) centralizava o vídeo numa caixa com o dobro da
      // altura visível e o aluno via faixa preta, vídeo cortado e nenhum
      // controle (HET-4164).
      if (
        !isWeb &&
        (media?.type === 'Video' || media?.type === 'WebinarHerospark')
      ) {
        return playerFillHeight ?? mediaRenderWrapperHeight
      }
      if (screenMedia.gtSm) return windowHeight * 0.6
      if (media?.type === 'Video') return mediaRenderWrapperHeight
      if (media?.type === 'Audio') return 184
      return 450
    })()
    return { width: computedWidth, height: computedHeight }
  }, [
    screenMedia.gtSm,
    screenMedia.gtMd,
    windowWidth,
    horizontalPadding,
    media?.type,
    windowHeight,
    mediaRenderWrapperHeight,
    playerFillHeight,
  ])

  // Caixa do player de vídeo no web: largura fluida (100%) e altura vinda do
  // layout (`playerFillHeight`, consumo desktop) ou, na falta dela — tablet e
  // celular, no arranjo compacto do LessonLayout —, do aspect-ratio 16:9.
  //
  // Sem o aspect-ratio esse caminho caía num par fixo de 576px de largura com
  // `windowHeight * 0.6` de altura: em tablet o player parava de acompanhar o
  // container (sobrava faixa vazia na horizontal) e a altura arbitrária
  // estourava o limite do wrapper, cortando os controles.
  const webVideoContainerStyle = playerFillHeight
    ? { width: '100%' as const, height: playerFillHeight }
    : { width: '100%' as const, aspectRatio: WEB_VIDEO_ASPECT_RATIO }

  // O container acima já define a caixa; o player só a preenche.
  const webVideoStyle = { width: '100%' as const, height: '100%' as const }

  const renderHtmlContent = useMemo(() => {
    if (!media.text) return null
    return (
      // `width` é um valor fixo por breakpoint (958/576), então imagens, tabelas
      // e larguras percentuais do HTML eram dimensionadas contra um número que
      // não corresponde ao espaço real — que varia com a lista de aulas aberta
      // ou fechada. Medimos o container e usamos o fixo só até o primeiro
      // layout.
      <View
        width="100%"
        onLayout={(event) => {
          const layoutWidth = Math.round(event.nativeEvent.layout.width)
          setTextContentWidth((previous) =>
            layoutWidth > 0 ? layoutWidth : previous,
          )
        }}
      >
        <RenderHtml
          contentWidth={textContentWidth || width}
          source={{ html: processHtmlContent(media.text) }}
          // Sem estes dois props o RenderHtml apaga qualquer <iframe> colado no
          // HTML da aula (ver HtmlIframeRenderer).
          customHTMLElementModels={htmlIframeElementModels}
          renderers={htmlIframeRenderers}
          baseStyle={{
            color: textBodyColor,
            paddingBottom: 8,
          }}
          enableExperimentalBRCollapsing
          enableExperimentalGhostLinesPrevention
          defaultTextProps={{
            selectable: true,
          }}
        />
      </View>
    )
  }, [media.text, width, textContentWidth, textBodyColor])

  // O react-youtube recarrega o vídeo sempre que `playerVars.start` muda. O
  // `startMediaFrom` muda com o player vivo: ao concluir a aula ele vira 0, e a
  // aula retomada do meio pulava de volta para o início. O início só é lido
  // quando a mídia troca.
  const youtubeStartFrom = useFirstValuePerKey(startMediaFrom, media?.id)
  const opts = useMemo(
    () => ({
      height: height.toString(),
      width: width.toString(),
      playerVars: {
        controls: 1,
        start: youtubeStartFrom ? Math.floor(youtubeStartFrom) : undefined,
      },
    }),
    [height, width, youtubeStartFrom],
  )

  const handleProgressUpdate = useCallback(
    (params: IUpdateLessonProgress) => {
      updateLessonProgress?.(params)
    },
    [updateLessonProgress],
  )

  // --- Instrumentação de progresso dos players web ---
  // Spark Videos: o iframe emite `time_update` por postMessage (mesmo protocolo
  // do embed legado); escutamos no web independente do player visível.
  useSparkVideosIframeProgress({
    enabled: isWeb,
    onProgress: handleProgressUpdate,
    resetKey: media?.id,
  })

  // Reporter por quarter reutilizado por YouTube e Áudio no web.
  const reportQuarter = useQuarterProgressReporter(
    handleProgressUpdate,
    media?.id,
  )

  // Vimeo web: iframe controlado + JS API por postMessage.
  const isWebVimeo = Boolean(
    isWeb && media?.type === 'Video' && fileUrl?.includes('vimeo'),
  )
  const vimeoIframeRef = useRef<HTMLIFrameElement>(null)
  useVimeoIframeProgress({
    iframeRef: vimeoIframeRef,
    enabled: isWebVimeo,
    onProgress: handleProgressUpdate,
    resetKey: media?.id,
  })

  // YouTube web: react-youtube expõe player ref + eventos de estado.
  const ytPlayerRef = useRef<YouTubePlayer | null>(null)
  const ytIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const clearYtInterval = useCallback(() => {
    if (ytIntervalRef.current) {
      clearInterval(ytIntervalRef.current)
      ytIntervalRef.current = null
    }
  }, [])

  const handleYoutubeReady = useCallback((event: YouTubeEvent) => {
    ytPlayerRef.current = event.target
  }, [])

  const handleYoutubeStateChange = useCallback(
    (event: YouTubeEvent<number>) => {
      // 1 = playing (YT.PlayerState.PLAYING)
      if (event.data === 1) {
        clearYtInterval()
        ytIntervalRef.current = setInterval(async () => {
          const player = ytPlayerRef.current
          if (!player) return
          const [time, duration] = await Promise.all([
            player.getCurrentTime(),
            player.getDuration(),
          ])
          reportQuarter(time, duration)
        }, 1000)
      } else {
        clearYtInterval()
      }
    },
    [clearYtInterval, reportQuarter],
  )

  const handleYoutubeEnd = useCallback(async () => {
    clearYtInterval()
    const player = ytPlayerRef.current
    if (!player) return
    const duration = await player.getDuration()
    reportQuarter(duration, duration)
  }, [clearYtInterval, reportQuarter])

  useEffect(() => clearYtInterval, [clearYtInterval])

  const videoPlaylist = useCallback(async () => {
    if (!media.organizationUuid || media.host !== MEDIA_TYPES.SPARK_VIDEOS)
      return
    try {
      setIsLoadingVideo(true)
      setSparkVideosError(false)
      const response = await sparkVideosClient.get(
        `/api/player/${media.organizationUuid}/${media.hostKey}/urls`,
      )
      setSparkVideosUrls({
        mainUrl: response.data.main_url,
        qualities: response.data.qualities,
        thumbnailUrl: response.data.thumbnail,
        isVertical: response.data.width < response.data.height,
        width: response.data.width,
        height: response.data.height,
      })
    } catch (error) {
      console.log('error', error)
      setSparkVideosUrls(null)
      setSparkVideosError(true)
    } finally {
      setIsLoadingVideo(false)
    }
  }, [media.organizationUuid, media.host, media.hostKey])

  const qualityOptions = useMemo(() => {
    return generateVideoQualityOptions(sparkVideosUrls)
  }, [sparkVideosUrls])

  useEffect(() => {
    if (media.host === MEDIA_TYPES.SPARK_VIDEOS && media.hostKey && media.id) {
      videoPlaylist()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media.host, media.hostKey, media.id])

  const { subdomainURL } = useDomain()

  const styles = useMemo(() => {
    return StyleSheet.create({
      container: {
        flex: 1,
        width,
        height,
      },
      video: {
        alignSelf: 'center',
        width: '100%',
        height,
        borderRadius: videoBorderRadius,
        overflow: 'hidden',
      },
    })
  }, [width, height, videoBorderRadius])

  // Vimeo id + privacy hash. Unlisted videos need the hash in the embed URL
  // (`?h=`), otherwise the player answers 403. The hash lives in fileUrl
  // (the pasted share link); host_key only holds the bare id.
  const vimeoSource = useMemo(() => parseVimeoSource(fileUrl), [fileUrl])

  // Só mostra spinner enquanto a request está em voo. Falha (urls null +
  // sparkVideosError) cai num estado de erro com retry — sem spinner eterno.
  const shouldShowLoading =
    (isLoading || (isMediaSparkVideos && isLoadingVideo)) &&
    media.type !== 'Text'

  if (shouldShowLoading) {
    const loadingHeight =
      media.type === 'Video' || media.type === 'Audio'
        ? mediaRenderWrapperHeight
        : 450
    return (
      <View
        justifyContent="center"
        alignItems="center"
        height={loadingHeight}
        gap="$spacing-inset-sm"
      >
        <Spinner color="$icon-primary" size="large" />
        <Text color="$text-primary">Carregando conteúdo...</Text>
      </View>
    )
  }

  if (media === null) return null

  if (isMediaSparkVideos) {
    if (sparkVideosUrls && canShowVideo && !isWeb) {
      return (
        <View
          position="relative"
          width="100%"
          height={mediaRenderWrapperHeight}
        >
          <VideoPlayer
            videoUrl={sparkVideosUrls}
            qualityOptions={qualityOptions}
            title={media.title}
            videoContainerHeight={mediaRenderWrapperHeight}
            thumbnailUrl={thumbnailUrl || sparkVideosUrls?.thumbnailUrl}
            mediaUrl={sparkVideosUrls?.mainUrl}
            isVertical={sparkVideosUrls?.isVertical}
            updateProgress={handleProgressUpdate}
            actualProgress={progress}
            videoStartTime={startMediaFrom}
          />
        </View>
      )
    }
    if (canShowVideo && sparkVideosUrls && isWeb) {
      return (
        <View
          style={isWebCourseVideo ? webVideoContainerStyle : styles.container}
        >
          <SparkVideosPlayerIframe
            key={media.id}
            media={media}
            startMediaFrom={startMediaFrom}
            style={{
              ...(isWebCourseVideo ? webVideoStyle : { width, height }),
              border: 0,
            }}
          />
        </View>
      )
    }

    // Falha ao buscar as URLs do player (ex.: 503 transiente do SparkVideos).
    // Mostra erro + retry em vez de spinner/null eterno.
    if (sparkVideosError) {
      return (
        <View
          justifyContent="center"
          alignItems="center"
          height={playerFillHeight ?? mediaRenderWrapperHeight}
          gap="$spacing-inset-sm"
        >
          <HeroIcon
            icon={['fal', 'triangle-exclamation']}
            size={40}
            color="$text-muted"
          />
          <Text color="$text-primary" textAlign="center">
            Não foi possível carregar o vídeo.
          </Text>
          <Button variant="primary" outline onPress={() => videoPlaylist()}>
            <Text color="$accent" ff="$mulishBold" fontSize="$font-size-xxs">
              Tentar novamente
            </Text>
          </Button>
        </View>
      )
    }

    return null
  }

  if (isDownloadOnlyFile) {
    // Web: ocupa 100% da largura e usa a altura do player (playerFillHeight)
    // para o bloco de download centralizar e o botão aparecer por completo.
    const downloadOnlyFill =
      isWeb && location !== 'extraContent' && Boolean(playerFillHeight)
    return (
      <View
        style={
          downloadOnlyFill
            ? {
                width: '100%',
                height: playerFillHeight,
                justifyContent: 'center',
                alignItems: 'center',
              }
            : isWeb
              ? [styles.container, { justifyContent: 'center' }]
              : // Mobile: sem altura fixa; o card do NotSupported define a
                // própria altura (aspecto 16:9) conforme o Figma.
                { width: '100%' }
        }
      >
        <NotSupported media={media} fileUrl={fileUrl} />
      </View>
    )
  }
  if (media.type === 'WebinarHerospark' && media.originUrl) {
    if (isWeb) {
      const webinarFill =
        location !== 'extraContent' && Boolean(playerFillHeight)
      return (
        <View
          style={
            webinarFill
              ? { width: '100%', height: playerFillHeight }
              : styles.container
          }
        >
          <iframe
            src={toEmbeddableUrl(media.originUrl)}
            style={{
              ...(webinarFill ? webVideoStyle : { width, height }),
              border: 0,
            }}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
          ></iframe>
        </View>
      )
    }
    return (
      <View style={styles.video}>
        <WebView
          source={{ uri: toEmbeddableUrl(media.originUrl) }}
          style={{
            borderRadius: videoBorderRadius,
            backgroundColor: 'transparent',
          }}
          allowsFullscreenVideo
          // iOS defaults <video> to the native fullscreen player; inline keeps
          // the lesson layout (parity with the Panda/Moviie WebView below).
          allowsInlineMediaPlayback
        />
      </View>
    )
  }

  if (media.type === 'Document' && fileUrl) {
    if (isMediaPdf && isWeb) {
      const pdfFill = location !== 'extraContent' && Boolean(playerFillHeight)
      return (
        <View
          style={
            pdfFill ? { width: '100%', height: playerFillHeight } : undefined
          }
        >
          <iframe
            src={
              buildPdfViewerUrl({
                courseId,
                mediaId: media.id,
                fileUrl,
              }) ?? undefined
            }
            style={{
              ...(pdfFill
                ? { width: '100%', height: playerFillHeight }
                : { width, height: height < 700 ? 450 : height }),
              border: 0,
            }}
          />
        </View>
      )
    }

    if (isMediaPdf && !isWeb) {
      return <PdfViewer fileUrl={fileUrl} closeModal={() => closeModal?.()} />
    }
  }

  if (media.type === 'Audio' && fileUrl) {
    // Mesmo player (expo-audio) no nativo e no web — substitui o <audio> HTML.
    // O AudioPlayer centraliza o conteúdo (horizontal + vertical) internamente.
    return (
      <View
        overflow="hidden"
        borderRadius="$border-radius-md"
        height={playerFillHeight ?? height}
        width="100%"
        backgroundColor="$bg-surface-strong"
        alignItems="center"
        justifyContent="center"
      >
        <AudioPlayer
          url={fileUrl}
          updateProgress={handleProgressUpdate}
          actualProgress={progress}
          audioStartTime={startMediaFrom}
        />
      </View>
    )
  }

  if (media.type === 'Text' && media.text) {
    return renderHtmlContent
  }

  if (media.type === 'Image' && fileUrl) {
    if (isImageVariant) {
      return (
        <>
          {isExpandedViewPage ? (
            <ZoomableImage
              uri={fileUrl}
              width={windowWidth - windowWidth * 0.01}
              height={windowHeight - windowHeight * 0.1}
              objectFit="contain"
            />
          ) : (
            <Image
              alt={media.title}
              source={{
                uri: fileUrl,
              }}
              style={{
                width,
                height,
              }}
              resizeMode="cover"
            />
          )}
        </>
      )
    }

    const handlePress = () => {
      if (needFloatingExpandButton) {
        closeModal?.()
        setTimeout(() => {
          handleNavigateToImageViewer()
        }, 5)
      }
    }

    const isWebFillImage =
      isWeb && location !== 'extraContent' && Boolean(playerFillHeight)

    return (
      <View
        position="relative"
        onPress={handlePress}
        style={
          isWebFillImage
            ? {
                width: '100%',
                height: playerFillHeight,
                alignItems: 'center',
                justifyContent: 'center',
              }
            : undefined
        }
      >
        <Image
          alt={media.title}
          source={{
            uri: fileUrl,
          }}
          style={
            isWebFillImage
              ? { width: '100%', height: playerFillHeight }
              : { height, width }
          }
          resizeMode={isWebFillImage ? 'contain' : 'cover'}
        />
        {needFloatingExpandButton && (
          <FloatingExpandButton onPress={handlePress} />
        )}
      </View>
    )
  }

  // Moviie is the provider's UNIVERSAL player — the package resolves the
  // rendition per platform (expo-video + native chrome on iOS/Android, the
  // official watch embed iframe on web). It authorizes itself from
  // hostKey (+ publishableKey for native), so it never needs originUrl —
  // select it before the originUrl-gated embed block below so a Moviie video
  // with a valid key but a blank originUrl still gets the native player.
  // The publishable key is required for native playback but optional on web;
  // a key-less native payload (school connected before key provisioning, or
  // the fetch failed) falls through to the originUrl WebView fallback.
  if (
    media &&
    media.type === 'Video' &&
    media.host === MEDIA_TYPES.MOVIIE &&
    media.hostKey &&
    (isWeb || media.providerKey)
  ) {
    // No desktop o player preenche a altura que o layout reserva para ele
    // (playerFillHeight), como os demais vídeos do web.
    return (
      <View
        style={
          isWebCourseVideo && playerFillHeight
            ? { ...styles.video, height: playerFillHeight }
            : styles.video
        }
      >
        <MoviieLessonPlayer
          embedId={media.hostKey}
          publishableKey={media.providerKey}
          updateProgress={handleProgressUpdate}
        />
      </View>
    )
  }

  // Embed-based video hosts (PandaVideos, Moviie, generic "HTML livre") keep type === 'Video'
  // so they inherit the video-lesson features, but the playable content lives in
  // originUrl rather than fileUrl — handle them before the fileUrl gate below.
  if (
    media &&
    media.type === 'Video' &&
    (media.host === MEDIA_TYPES.PANDAVIDEOS ||
      media.host === MEDIA_TYPES.MOVIIE ||
      media.host === MEDIA_TYPES.EMBED) &&
    media.originUrl
  ) {
    if (isWeb) {
      if (media.host === MEDIA_TYPES.EMBED) {
        return (
          <View style={styles.container}>
            <iframe
              srcDoc={withEmbedReset(media.originUrl)}
              sandbox={EMBED_SANDBOX}
              style={{ width, height, border: 0 }}
              allowFullScreen
            />
          </View>
        )
      }
      // Trusted third-party player origin (Pandavideos / Moviie watch embed):
      // sandbox it like a video player — scripts + presentation for casting,
      // same-origin so the player can reach its OWN player API/storage on its
      // own origin (safe here because the origin is a known provider, not
      // creator-supplied markup). Deliberately omits allow-forms and
      // allow-top-navigation so the framed player can't drive app navigation.
      return (
        <View style={styles.container}>
          <iframe
            src={media.originUrl}
            sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
            style={{ width, height, border: 0 }}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
          />
        </View>
      )
    }
    return (
      <View style={styles.video}>
        <WebView
          source={
            media.host === MEDIA_TYPES.EMBED
              ? { html: withEmbedReset(media.originUrl) }
              : { uri: media.originUrl }
          }
          style={{
            borderRadius: videoBorderRadius,
            backgroundColor: 'transparent',
          }}
          // Native counterpart of the web EMBED_SANDBOX: this WebView renders
          // untrusted creator-supplied markup (host=Embed) or a third-party
          // player URL, so isolate it from the app. incognito drops persistent
          // storage/cookies for its lifetime, the cookie flags stop it sharing
          // or writing app cookies, and originWhitelist keeps in-app navigation
          // to secure origins (anything else opens in the system browser).
          // JavaScript stays enabled — free embeds legitimately need it; the
          // goal is isolation, not blocking scripts.
          incognito
          sharedCookiesEnabled={false}
          thirdPartyCookiesEnabled={false}
          originWhitelist={['https://*']}
          allowsFullscreenVideo
          // iOS defaults <video> to the native fullscreen player; inline keeps
          // the lesson layout (parity with how these embeds behave on Android).
          allowsInlineMediaPlayback
        />
      </View>
    )
  }

  if (media && media.type === 'Video' && fileUrl) {
    if (isWeb) {
      // Decide pelo host, não pela URL: links curtos (youtu.be) não contêm
      // "youtube" e caíam no fallback SparkVideos → "Processando Conteúdo".
      if (media.host === 'Youtube' && media.hostKey) {
        return (
          <View
            style={
              isWebCourseVideo
                ? { ...webVideoContainerStyle, alignSelf: 'center' }
                : [styles.video, { alignSelf: 'center', height }]
            }
          >
            <YouTube
              videoId={media.hostKey}
              id="video"
              opts={
                isWebCourseVideo
                  ? { ...opts, width: '100%', height: '100%' }
                  : opts
              }
              onReady={handleYoutubeReady}
              onStateChange={handleYoutubeStateChange}
              onEnd={handleYoutubeEnd}
              style={{
                display: 'flex',
                flex: 1,
              }}
            />
          </View>
        )
      } else if (fileUrl.includes('vimeo') && vimeoSource) {
        return (
          <View
            style={
              isWebCourseVideo
                ? { ...webVideoContainerStyle, alignSelf: 'center' }
                : [styles.container, { alignSelf: 'center' }]
            }
          >
            <iframe
              // Sem o ref o hook de progresso não alcança o `contentWindow` e
              // nunca assina o `timeupdate` — diferente do Spark Videos, que
              // empurra o progresso sozinho, o Vimeo só emite para quem pediu.
              ref={vimeoIframeRef}
              src={buildVimeoPlayerUrl(vimeoSource, { api: 1 })}
              width={isWebCourseVideo ? '100%' : width}
              height={isWebCourseVideo ? '100%' : height}
              style={{ border: 0 }}
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            />
          </View>
        )
      } else {
        return (
          <>
            {sparkVideosUrls ? (
              <View
                style={
                  isWebCourseVideo ? webVideoContainerStyle : styles.container
                }
              >
                <SparkVideosPlayerIframe
                  key={media.id}
                  media={media}
                  startMediaFrom={startMediaFrom}
                  style={{
                    ...(isWebCourseVideo ? webVideoStyle : { width, height }),
                    border: 0,
                  }}
                />
              </View>
            ) : (
              <ProcessingVideoInformative />
            )}
          </>
        )
      }
    } else {
      if (media.host === 'Youtube' && media.hostKey) {
        return (
          <View style={[styles.video, { height: mediaRenderWrapperHeight }]}>
            <YoutubeVideoPlayer
              height={mediaRenderWrapperHeight}
              videoId={media.hostKey}
              webViewStyle={{
                borderRadius: videoBorderRadius,
              }}
              videoStartTime={startMediaFrom}
              updateProgress={handleProgressUpdate}
              actualProgress={progress}
            />
          </View>
        )
      } else if (media.host === 'Vimeo') {
        return (
          <View style={styles.video}>
            <VimeoVideoPlayer
              domain={subdomainURL || ''}
              videoId={vimeoSource?.id || ''}
              videoHash={vimeoSource?.hash}
              videoStartTime={startMediaFrom}
              updateProgress={handleProgressUpdate}
              actualProgress={progress}
            />
          </View>
        )
      }
    }
  }

  return (
    <View style={[styles.container, { justifyContent: 'center' }]}>
      {children}
    </View>
  )
}
